import type { ReactNode } from "react";
import { useAuth } from "./auth";
import type { Me } from "./types";

/**
 * 화면 권한 판단은 여기 하나로 모은다. 기준은 서버가 내려준 권한 코드(/v1/auth/me)뿐이다.
 * 화면에서 숨기는 것은 편의일 뿐이고, 실제 차단과 테넌트 범위·필드 걸러내기는 서버가 한다.
 */
export function can(me: Me | null | undefined, ...perms: string[]): boolean {
  return !!me && perms.some((p) => me.permissions.includes(p));
}

export function useMe(): Me | null {
  return useAuth((s) => s.me);
}

export function useCan(...perms: string[]): boolean {
  return can(useMe(), ...perms);
}

/** 권한이 있을 때만 children 을 그린다. */
export function Can({ perm, children, fallback = null }: { perm: string | string[]; children: ReactNode; fallback?: ReactNode }) {
  const ok = useCan(...(Array.isArray(perm) ? perm : [perm]));
  return ok ? children : fallback;
}

/** 이 고객사 화면을 볼 수 있나 (직원은 전부, 회원은 자기 고객사만, 머신은 /bridge 로만). */
export function canViewCustomer(me: Me | null, customerId: string): boolean {
  if (!me) return false;
  if (me.kind === "machine") return false;
  return me.isStaff || me.customerId === customerId;
}

/** 결제 업무만 하는 관리자 (작업·머신 화면이 필요 없는 사람): 작업 보기·머신 명령이 둘 다 없다. RBAC 라 역할 이름 대신 권한으로 본다. */
export function isBillingOnly(me: Me): boolean {
  return me.isStaff && !me.permissions.includes("TASK_DETAIL") && !me.permissions.includes("MACHINE_COMMAND");
}

/** 로그인 후 돌아갈 경로: 같은 사이트 안의 경로만 (외부 주소로 보내는 열린 리다이렉트 방지). */
export function safeRedirect(path: unknown): string | undefined {
  if (typeof path !== "string") return undefined;
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\") || path.startsWith("/login")) return undefined;
  return path;
}
